import { create } from "zustand";
import { STORAGE_KEYS } from "../config/constants";
import useTransactionStore from "./useTransactionStore";
import useCartStore from "./useCartStore";
import useSessionStore from "./useSessionStore";
import sqliteImmediateStorage from "./sqliteImmediateStorage";

// tillData is read through sqliteImmediateStorage (SQLite via Electron IPC,
// falling back to localStorage only when that IPC isn't available) instead
// of raw localStorage. Confirmed via boot trace: on refresh in CW Browser,
// tillSession/posConfig (already SQLite-backed) reliably survive while
// tillData (previously localStorage-only) came back empty every time —
// the localStorage the webview sees after a refresh isn't reliable, but the
// on-disk SQLite database (same one tillSession/posConfig already trust) is.
const getTillDataFromStorage = async () => {
  try {
    const raw = await sqliteImmediateStorage.getItem(STORAGE_KEYS.TILL_DATA);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// tillValue (till identity used to scope Sales History etc.) had the same
// raw-localStorage fragility as tillData did before BUG-1700 — CW Browser's
// webview refresh can wipe localStorage while the SQLite-backed store
// survives. Routed through sqliteImmediateStorage for the same reason.
const getTillValueFromStorage = async () => {
  try {
    const raw = await sqliteImmediateStorage.getItem(STORAGE_KEYS.TILL_VALUE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getJSONFromStorage = (key) => {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : null;
  } catch {
    return null;
  }
};

// BUG-1612: tillValue.cwr_till_id is only ever written once, from the till-tile
// click on the Select-a-Till screen (Till.js) — ordinary login/logout never
// re-derives it, and BUG-1564's same-day fast-forward (straight to /pos) skips
// that screen entirely for an already-linked till. So if tillValue was ever lost
// (e.g. pre-BUG-1612 localStorage wipe), nothing in normal usage repairs it —
// the till stays "linked" but every tillId-scoped query (Sales History, sync,
// eBill) silently filters on the literal string "undefined" forever.
// tillData carries the same identifier (tillAccess.cwrTill.cwrTillID) and is
// reliably persisted, so use it to self-heal tillValue whenever the latter is
// missing cwr_till_id.
const repairTillValue = (tillValue, tillData) => {
  if (tillValue?.cwr_till_id || !tillData?.tillAccess?.cwrTill?.cwrTillID) return tillValue;
  const repaired = {
    ...(tillValue || {}),
    cwr_till_id: tillData.tillAccess.cwrTill.cwrTillID,
    name: tillValue?.name || tillData.tillAccess.cwrTill.till || null,
    cSBunitID: tillValue?.cSBunitID || tillData.tillAccess.csBunit?.csBunitId || null,
  };
  sqliteImmediateStorage.setItem(STORAGE_KEYS.TILL_VALUE, JSON.stringify(repaired));
  return repaired;
};

const useAuthStore = create((set, get) => ({
  // State — initialized as null; populated by rehydrateFromStorage() after
  // tenant storage prefix is applied (avoids reading wrong tenant's data).
  user: null,
  tokens: {
    accessToken: null,
    refreshToken: null,
  },
  isAuthenticated: false,
  tillId: null,
  tillName: null,
  storeId: null,
  tillData: null,
  userData: null,
  tillValue: null,
  username: null,

  // Actions
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  setTokens: ({ accessToken, refreshToken }) => {
    if (refreshToken) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }
    set({ tokens: { accessToken, refreshToken } });
  },

  setTillData: (tillData) => {
    sqliteImmediateStorage.setItem(STORAGE_KEYS.TILL_DATA, JSON.stringify(tillData));
    const tillId = tillData?.tillAccess?.cwrTill?.till || null;
    const storeId = tillData?.tillAccess?.csBunit?.csBunitId || null;
    const tillValue = repairTillValue(get().tillValue, tillData);
    set({ tillData, tillId, storeId, tillValue });

    // Push config to main-process background order sync
    if (window.electronAPI?.sync?.setOrderSyncConfig && tillData?.tillAccess) {
      const ta = tillData.tillAccess;
      window.electronAPI.sync.setOrderSyncConfig({
        serverUrl: window.__CW_TENANT_URLS__?.REACT_APP_serverUrl || process.env.REACT_APP_serverUrl,
        tillId: ta.cwrTill?.cwrTillID,
        userId: ta.csUserId,
        clientId: ta.csClientId,
        bunitId: ta.csBunit?.csBunitId,
        doctypeId: ta.csBunit?.cwrCsDoctypeId,
        pricelistId: ta.csBunit?.cwrSpricelistId,
        warehouseId: ta.csBunit?.mWarehouse?.mWarehouseID,
        currencyId: ta.csBunit?.currencies?.[0]?.csCurrencyId,
        taxIncludeFlag: ta.csBunit?.isTaxIncluded || "Y",
        precision: ta.csBunit?.currencies?.[0]?.prcPrecision || 2,
        customerAddressId: ta.csBunit?.customerAddress?.sCustomerAddressID,
        b2cCustomerId: ta.csBunit?.b2cCustomer?.sCustomer?.sCustomerID,
      }).catch(() => {});
    }
  },

  setTillId: (tillId) => {
    localStorage.setItem(STORAGE_KEYS.TILL_ID, tillId);
    set({ tillId });
  },

  setTillName: (tillName) => {
    localStorage.setItem(STORAGE_KEYS.TILL_NAME, tillName);
    set({ tillName });
  },

  setUserData: (data) => {
    localStorage.setItem("userData", JSON.stringify(data));
    set({ userData: data });
  },

  setTillValue: (value) => {
    sqliteImmediateStorage.setItem(STORAGE_KEYS.TILL_VALUE, JSON.stringify(value));
    set({ tillValue: value });
  },

  setUsername: (name) => {
    localStorage.setItem("username", name);
    set({ username: name });
  },

  // Re-read all keys from storage into the store.
  // Must be called after tenant storage monkey-patch is applied on reload.
  // Validates tillData has expected shape — discards corrupt/stale data
  // so the app falls through to a fresh API fetch on the Till page.
  // Async: tillData is read via sqliteImmediateStorage, which awaits an
  // Electron IPC round-trip to the main-process SQLite DB (falling back to
  // localStorage only when that IPC isn't available).
  rehydrateFromStorage: async () => {
    const tillData = await getTillDataFromStorage();
    const storedTillValue = await getTillValueFromStorage();
    const isValid = tillData && tillData.tillAccess && tillData.tillAccess.cwrTill;
    const tillValue = isValid ? repairTillValue(storedTillValue, tillData) : storedTillValue;

    const tillId = isValid ? (tillData.tillAccess.cwrTill.till || null) : (localStorage.getItem(STORAGE_KEYS.TILL_ID) || null);
    const tillName = localStorage.getItem(STORAGE_KEYS.TILL_NAME) || null;
    const storeId = isValid ? (tillData.tillAccess.csBunit?.csBunitId || null) : null;
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || null;

    if (tillData && !isValid) {
      console.warn("[AuthStore] Stale/corrupt tillData detected — discarding");
      sqliteImmediateStorage.removeItem(STORAGE_KEYS.TILL_DATA);
    }

    set({
      tillData: isValid ? tillData : null,
      tillId,
      tillName,
      storeId,
      tokens: { accessToken: get().tokens.accessToken, refreshToken },
      userData: getJSONFromStorage("userData"),
      tillValue,
      username: localStorage.getItem("username") || null,
    });
  },

  // Logout clears user-specific state but preserves till binding.
  // The till is physical hardware shared by multiple cashiers — it stays
  // linked until explicitly unlinked via unlinkTill().
  logout: () => {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem("userData");
    localStorage.removeItem("username");
    localStorage.removeItem("cartObj");
    localStorage.removeItem("exchangeCartObj");
    localStorage.removeItem("walletDataForCardIntegration");
    localStorage.removeItem("omsOrderDetails");
    localStorage.removeItem("giftCardIntegrationTopupData");
    localStorage.removeItem("callCenterHoldQueue");
    localStorage.removeItem("isPanCustomer");
    localStorage.removeItem("isNriBilling");
    localStorage.removeItem("isCreditSale");
    localStorage.removeItem("isB2bEnabled");
    useTransactionStore.getState().setIsCreditSale(null);
    useTransactionStore.getState().setIsB2bEnabled(null);
    useTransactionStore.getState().setWalletPayments([]);
    useTransactionStore.getState().setSelectedReturnType(null);
    useTransactionStore.getState().setSalesReturn("N");
    useCartStore.getState().setExchangeCart(null);
    localStorage.removeItem("customerAddressIds");
    // lastUpdatedTime is intentionally NOT cleared here — the till/catalog sync
    // state is independent of the user session. Login.js always calls processSync,
    // which uses lastUpdatedTime to run an incremental (not full) sync when the
    // till was already synced. Clearing it here would force a full sync on every
    // ordinary login (BUG: full sync on every login for a previously linked till).
    // tillValue is intentionally NOT cleared — till stays linked to device
    set({
      user: null,
      tokens: { accessToken: null, refreshToken: null },
      isAuthenticated: false,
      userData: null,
      username: null,
      // tillId, tillName, storeId, tillData, tillValue preserved — till binding
      // survives logout (see comment above); only user-identity state is cleared.
    });
  },

  // Unlink till: clears everything including till binding.
  // After this, user must select a till on next login.
  unlinkTill: () => {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    sqliteImmediateStorage.removeItem(STORAGE_KEYS.TILL_DATA);
    localStorage.removeItem(STORAGE_KEYS.TILL_ID);
    localStorage.removeItem(STORAGE_KEYS.TILL_NAME);
    sqliteImmediateStorage.removeItem(STORAGE_KEYS.TILL_VALUE);
    localStorage.removeItem("userData");
    localStorage.removeItem("username");
    // A different till may be linked next — force a full sync for it.
    useSessionStore.getState().setLastUpdatedTime(null);
    set({
      user: null,
      tokens: { accessToken: null, refreshToken: null },
      isAuthenticated: false,
      tillId: null,
      tillName: null,
      storeId: null,
      tillData: null,
      userData: null,
      tillValue: null,
      username: null,
    });
  },

  // Selectors
  getPrecision: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
  },

  getStoreId: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.csBunitId || null;
  },

  getPaymentMethods: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.paymentMethodList || [];
  },

  getDefaultCustomer: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.b2cCustomer || null;
  },

  getTillConfig: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.cwrTill || null;
  },

  getCurrencyType: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.currencies?.[0] || null;
  },

  getRoundingMethod: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.currencies?.[0]?.roundingMethod || "NR";
  },

  getTaxIncludeFlag: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit?.isTaxIncluded || "Y";
  },

  getTillAccess: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess || null;
  },

  getUserAccessController: () => {
    const tillData = get().tillData;
    try {
      return JSON.parse(tillData?.tillAccess?.userAccessController || "{}");
    } catch {
      return {};
    }
  },

  getPrintTemplate: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.cwrTill?.printTemplate || null;
  },

  getHardwareController: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.cwrTill?.hardwareController || null;
  },

  getBusinessUnit: () => {
    const tillData = get().tillData;
    return tillData?.tillAccess?.csBunit || null;
  },
}));

export default useAuthStore;
